import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, Radio, Timer, ArrowRight, Check, ChevronDown,
  Target, Unlock, Compass, TrendingUp, Loader2
} from 'lucide-react';
import SNAPSHOT from '@/landing-snapshot.json';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_build-on-prompt/artifacts/4nyosk34_Logo%20-%20GrowUp1Again.png";

const BENEFIT_ICONS = [Target, Unlock, Compass, TrendingUp];

const CACHE_TTL = 24 * 60 * 60 * 1000;

function readCache(slug) {
  try {
    const raw = localStorage.getItem(`lp:${slug}`);
    if (!raw) return null;
    const { at, payload } = JSON.parse(raw);
    return Date.now() - at < CACHE_TTL ? payload : null;
  } catch { return null; }
}

function writeCache(slug, payload) {
  try { localStorage.setItem(`lp:${slug}`, JSON.stringify({ at: Date.now(), payload })); } catch { }
}

function Section({ children, className = '', id }) {
  return (
    <section id={id} className={`px-6 py-16 md:py-24 ${className}`}>
      <div className="max-w-6xl mx-auto">{children}</div>
    </section>
  );
}

export default function WebinarLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(() => readCache(slug) || SNAPSHOT[slug] || null);
  const [notFound, setNotFound] = useState(false);
  const [showRest, setShowRest] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({ full_name: '', whatsapp: '', email: '', describes: '', improve: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // index.html starts this request while the JS bundle is still downloading.
    const early = window.__LANDING_SLUG__ === slug && window.__LANDING__ ? window.__LANDING__ : null;
    const request = early || fetch(`${API}/landing/${slug}`).then(r => (r.ok ? r.json() : null));

    request.then(payload => {
      if (cancelled) return;
      if (payload) {
        setData(payload);
        writeCache(slug, payload);
      } else if (!readCache(slug)) {
        setNotFound(!SNAPSHOT[slug]);
      }
    }).catch(() => { if (!cancelled && !readCache(slug) && !SNAPSHOT[slug]) setNotFound(true); });

    return () => { cancelled = true; };
  }, [slug]);

  // Paint the hero first, then mount everything below the fold on the next frame.
  useEffect(() => {
    if (!data || showRest) return;
    const id = requestAnimationFrame(() => setShowRest(true));
    return () => cancelAnimationFrame(id);
  }, [data, showRest]);

  useEffect(() => {
    if (data?.page?.content?.meta_title) document.title = data.page.content.meta_title;
  }, [data]);

  const scrollToForm = () => {
    const payUrl = (data?.page?.content?.payment_url || '').trim();
    if (payUrl) { window.location.href = payUrl; return; }
    document.getElementById('reserve-seat')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API}/landing/${slug}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Registration failed. Please try again.');
      }
      // Conversion signal for Meta Pixel / GTM so ad platforms learn who registers.
      try {
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', { content_name: slug, content_category: form.improve || '' });
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'registration_complete',
          landing_slug: slug,
          profile_type: form.describes || '',
          improve_goal: form.improve || '',
        });
      } catch { /* analytics must never block the redirect */ }
      const tyKey = data?.page?.content?.thank_you_key || 'thank-you-registration';
      const tySegment = tyKey === 'thank-you-registration' ? 'registration'
        : tyKey === 'thank-you-payment' ? 'payment'
        : tyKey.replace(/^thank-you-/, '');
      navigate(`/thank-you/${tySegment}?slug=${slug}`);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="landing-not-found">
        <div className="text-center">
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase mb-2">Page not found</h1>
          <p className="text-white/40 text-sm">This webinar landing page is not available.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#FF1493]" size={28} />
      </div>
    );
  }

  const c = data.page.content || {};

  return (
    <div className="min-h-screen pb-24" data-testid="webinar-landing-page">
      {/* HERO — the page opens straight on the pain headline */}
      <Section className="pt-12 md:pt-16 pb-10">
        <div className="max-w-4xl">
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[1.05] mb-6">
            {c.hero_title}<br />
            <span className="gradient-text">{c.hero_title_accent}</span>
          </h1>
          <p className="text-base md:text-lg text-white/55 max-w-2xl mb-10 leading-relaxed">{c.hero_subtitle}</p>

          <div className="flex flex-wrap gap-4 mb-10">
            <div className="glass rounded-xl px-5 py-3 flex items-center gap-3" data-testid="hero-date">
              <Calendar size={16} className="text-[#FF1493]" />
              <span className="text-sm font-semibold">{c.session_date}</span>
            </div>
            <div className="glass rounded-xl px-5 py-3 flex items-center gap-3" data-testid="hero-time">
              <Clock size={16} className="text-[#00D4FF]" />
              <span className="text-sm font-semibold">{c.session_time}</span>
            </div>
            <div className="glass rounded-xl px-5 py-3 flex items-center gap-3">
              <Radio size={16} className="text-[#00FF9D] animate-pulse" />
              <span className="text-sm font-semibold">{c.session_host}</span>
            </div>
          </div>

          <button onClick={scrollToForm} data-testid="hero-cta" className="btn-neon px-10 py-4 rounded-xl text-sm inline-flex items-center gap-2 animate-pulse-glow">
            {c.cta_label} <ArrowRight size={16} />
          </button>
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/30 mt-5">{c.hero_badges}</p>
        </div>
      </Section>

      {/* BENEFITS */}
      {showRest && <>
      <Section className="border-t border-white/5">
        <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase mb-4">{c.benefits_title}</h2>
        <p className="text-white/50 max-w-2xl mb-12 text-sm md:text-base">{c.benefits_intro}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {(c.benefits || []).map((b, i) => {
            const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
            return (
              <div key={i} className="glass rounded-2xl p-6 hover:border-[#FF1493]/30 border border-transparent transition-colors" data-testid={`benefit-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF1493]/20 to-[#8B00FF]/20 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#FF1493]" />
                </div>
                <h3 className="text-base md:text-lg font-semibold mb-2">{b.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{b.text}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* FOR YOU IF */}
      <Section className="border-t border-white/5">
        <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase mb-10">{c.for_you_title}</h2>
        <div className="space-y-3 max-w-3xl">
          {(c.for_you || []).map((line, i) => (
            <div key={i} className="flex items-start gap-3 glass rounded-xl px-5 py-4" data-testid={`for-you-${i}`}>
              <Check size={16} className="text-[#00FF9D] mt-0.5 shrink-0" />
              <span className="text-sm text-white/70">{line}</span>
            </div>
          ))}
        </div>
        <p className="mt-8 text-base md:text-lg font-semibold gradient-text">{c.for_you_footer}</p>
      </Section>

      {/* MENTOR */}
      <Section className="border-t border-white/5">
        <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase mb-10">{c.mentor_title}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div>
            <div className="glass rounded-2xl p-6 text-center">
              {c.mentor_photo ? (
                <img src={c.mentor_photo} alt={c.mentor_name} loading="lazy" decoding="async" className="w-32 h-32 rounded-full mx-auto object-cover border-2 border-[#FF1493]/40" />
              ) : (
                <>
                  <div className="w-32 h-32 rounded-full mx-auto bg-gradient-to-br from-[#FF1493]/25 to-[#8B00FF]/25 border border-white/10 flex items-center justify-center">
                    <span className="font-['Barlow_Condensed'] text-4xl font-bold">{c.mentor_initials}</span>
                  </div>
                  <p className="text-[10px] text-white/25 mt-3 leading-snug">{c.mentor_photo_note}</p>
                </>
              )}
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase mt-4">{c.mentor_name}</h3>
              <p className="text-[11px] text-[#00D4FF] mt-1">{c.mentor_roles}</p>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="glass rounded-xl p-4 text-center">
                <p className="font-['Barlow_Condensed'] text-2xl font-bold gradient-text">{c.stat_1_value}</p>
                <p className="text-[10px] text-white/40 leading-tight mt-1">{c.stat_1_label}</p>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <p className="font-['Barlow_Condensed'] text-2xl font-bold gradient-text">{c.stat_2_value}</p>
                <p className="text-[10px] text-white/40 leading-tight mt-1">{c.stat_2_label}</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <p className="text-sm text-white/60 leading-relaxed">{c.mentor_hindi_1}</p>
            <p className="text-sm text-white/60 leading-relaxed">{c.mentor_hindi_2}</p>
            <p className="text-sm text-white/60 leading-relaxed">{c.mentor_hindi_3}</p>
            <p className="text-base md:text-lg italic text-white/80 border-l-2 border-[#FF1493] pl-4 py-1">{c.mentor_quote}</p>
            <p className="text-sm text-white/50 leading-relaxed">{c.mentor_bio_1}</p>
            <p className="text-sm text-white/50 leading-relaxed">{c.mentor_bio_2}</p>

            <div className="pt-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mb-3">{c.corporate_label}</p>
              <div className="flex flex-wrap gap-2">
                {(c.corporate_items || []).map(x => (
                  <span key={x} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70">{x}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mb-3">{c.ventures_label}</p>
              <div className="flex flex-wrap gap-2">
                {(c.ventures_items || []).map(x => (
                  <span key={x} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70">{x}</span>
                ))}
              </div>
            </div>
            <div className="glass rounded-xl px-5 py-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#00FF9D] mb-1">{c.founded_label}</p>
              <p className="text-sm font-semibold">{c.founded_name}</p>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {(c.mentor_tags || []).map(t => (
                <span key={t} className="px-3 py-1 rounded-full border border-[#FF1493]/25 bg-[#FF1493]/[0.06] text-[11px] text-white/60">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* WEBINAR DETAILS */}
      <Section className="border-t border-white/5">
        <div className="glass-heavy rounded-2xl p-7 md:p-10">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FF1493] mb-4">{c.details_eyebrow}</p>
          <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase leading-tight mb-8">{c.details_title}</h3>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'DATE', value: c.detail_date, Icon: Calendar, color: 'text-[#FF1493]' },
              { label: 'TIME', value: c.detail_time, Icon: Clock, color: 'text-[#00D4FF]' },
              { label: 'FORMAT', value: c.detail_format, Icon: Radio, color: 'text-[#00FF9D]' },
              { label: 'DURATION', value: c.detail_duration, Icon: Timer, color: 'text-[#8B00FF]' },
            ].map(({ label, value, Icon, color }) => (
              <div key={label} className="glass rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-wider text-white/30 mb-2">{label}</p>
                <div className="flex items-center gap-2">
                  <Icon size={14} className={color} />
                  <span className="text-sm font-semibold">{value}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {(c.detail_points || []).map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/65">
                <Check size={14} className="text-[#00FF9D] shrink-0" /> {p}
              </div>
            ))}
          </div>

          <button onClick={scrollToForm} data-testid="details-cta" className="btn-neon px-10 py-4 rounded-xl text-sm inline-flex items-center gap-2">
            {c.cta_label} <ArrowRight size={16} />
          </button>
        </div>
      </Section>

      {/* STEPS */}
      <Section className="border-t border-white/5">
        <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase mb-12">{c.steps_title}</h2>
        <div className="space-y-4">
          {(c.steps || []).map((s, i) => (
            <div key={i} className="flex gap-5 glass rounded-2xl p-6" data-testid={`step-${i}`}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF1493] to-[#8B00FF] flex items-center justify-center shrink-0 font-['Barlow_Condensed'] font-bold">
                {i + 1}
              </div>
              <div>
                <h3 className="text-base md:text-lg font-semibold mb-1">{s.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="border-t border-white/5">
        <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase mb-10">{c.faq_title}</h2>
        <div className="space-y-3 max-w-4xl">
          {(c.faqs || []).map((f, i) => (
            <div key={i} className="glass rounded-xl overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                data-testid={`faq-${i}`}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors"
              >
                <span className="text-sm font-medium">{f.q}</span>
                <ChevronDown size={16} className={`text-[#FF1493] shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && (
                <p className="px-5 pb-5 text-sm text-white/50 leading-relaxed" data-testid={`faq-answer-${i}`}>{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* REGISTRATION FORM */}
      <Section id="reserve-seat" className="border-t border-white/5">
        <div className="max-w-xl">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase mb-3">{c.form_title}</h2>
          <p className="text-sm text-white/50 mb-8 leading-relaxed">{c.form_subtitle}</p>

          <form onSubmit={submit} className="glass-heavy rounded-2xl p-6 md:p-8 space-y-5 text-left" data-testid="registration-form">
              {error && (
                <div className="p-3 rounded-lg bg-[#FF0055]/10 border border-[#FF0055]/30 text-[#FF0055] text-sm" data-testid="registration-error">{error}</div>
              )}
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">Full Name</label>
                <input required data-testid="reg-full-name" value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF1493]/50" placeholder="Your full name" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">WhatsApp Number</label>
                <input required type="tel" data-testid="reg-whatsapp" value={form.whatsapp} onChange={e => setForm(p => ({ ...p, whatsapp: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF1493]/50" placeholder="+91 9876543210" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">Email Address</label>
                <input required type="email" data-testid="reg-email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF1493]/50" placeholder="your@email.com" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">What best describes you? (optional)</label>
                <select data-testid="reg-describes" value={form.describes} onChange={e => setForm(p => ({ ...p, describes: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF1493]/50">
                  <option value="" className="bg-[#0a0a1a]">Select an option</option>
                  {(c.describes_options || []).map(o => <option key={o} value={o} className="bg-[#0a0a1a]">{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">What do you currently want to improve most? (optional)</label>
                <select data-testid="reg-improve" value={form.improve} onChange={e => setForm(p => ({ ...p, improve: e.target.value }))}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#FF1493]/50">
                  <option value="" className="bg-[#0a0a1a]">Select an option</option>
                  {(c.improve_options || []).map(o => <option key={o} value={o} className="bg-[#0a0a1a]">{o}</option>)}
                </select>
              </div>
              <button type="submit" disabled={submitting} data-testid="reg-submit"
                className="w-full btn-neon py-4 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                {submitting ? 'Reserving your seat...' : <>{c.form_cta} <ArrowRight size={16} /></>}
              </button>
              <p className="text-[11px] text-white/30 leading-relaxed">{c.form_disclaimer}</p>
            </form>
        </div>
      </Section>
      </>}

      {/* BRAND BLOCK — moved to the bottom so the page opens on the pain headline */}
      <footer className="px-6 pt-4 pb-10 border-t border-white/5" data-testid="landing-brand-footer">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-2">
          <img src={LOGO_URL} alt="GrowUp1Again" loading="lazy" decoding="async" className="h-20 w-auto object-contain mix-blend-screen" />
          <span className="font-['Barlow_Condensed'] text-lg md:text-xl font-bold uppercase tracking-wide gradient-text">
            {c.brand_line}
          </span>
        </div>
      </footer>

      {/* STICKY BOTTOM BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-[#050510]/95 backdrop-blur-xl px-5 py-3" data-testid="landing-sticky-bar">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{c.sticky_label}</p>
            <p className="text-[10px] uppercase tracking-wider text-white/35">{c.sticky_sub}</p>
          </div>

          {c.show_seats_left !== false && data.seats_left !== null && data.seats_left !== undefined && (
            <div className="hidden sm:block relative shrink-0" data-testid="seats-left-wrap">
              <div className="absolute -inset-3 rounded-full bg-[#FF1493]/35 blur-2xl animate-seat-aura pointer-events-none" aria-hidden />
              <div
                data-testid="seats-left-badge"
                className="relative flex items-center gap-2.5 px-5 py-2.5 rounded-full border bg-gradient-to-r from-[#FF1493]/20 to-[#8B00FF]/20 animate-soft-flash"
              >
                <span className="relative flex items-center justify-center w-2 h-2 shrink-0">
                  <span className="absolute w-2 h-2 rounded-full bg-[#FF1493] animate-ping" />
                  <span className="w-2 h-2 rounded-full bg-[#FF5AB4]" />
                </span>
                <span className="text-sm font-bold text-white whitespace-nowrap tracking-wide">
                  {data.seats_left > 0 ? `Only ${data.seats_left} seats left` : (c.seats_full_text || 'Registrations closing soon')}
                </span>
              </div>
            </div>
          )}
          <button onClick={scrollToForm} data-testid="sticky-cta" className="btn-neon px-5 md:px-8 py-3 rounded-xl text-xs md:text-sm whitespace-nowrap shrink-0">
            {c.cta_label}
          </button>
        </div>
      </div>
    </div>
  );
}
